import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { HaleWordmark } from '../components/HaleBrand';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  ShieldCheck, Plus, Flask, X, ArrowsClockwise, CheckCircle, Warning,
  WarningCircle, Eye, Buildings,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =========================================================================
 *  Hale Admin Console — /assessor/hale
 *
 *  Three tabs: Brands · SKU Queue · Detail (when a SKU is opened).
 *  Reuses the existing ToxIntel scientific engines under the hood.
 * =======================================================================*/

const VERDICT_STYLE = {
  no_concern:            { tone: 'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]', Icon: CheckCircle,    label: 'No significant concern' },
  monitor:               { tone: 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]', Icon: Warning,        label: 'Monitor' },
  further_review:        { tone: 'bg-[#FFE5D9] text-[#9A3412] border-[#FDBA8C]', Icon: Warning,        label: 'Further review' },
  high_priority_concern: { tone: 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]', Icon: WarningCircle,  label: 'Escalation Review Recommended' },
};

const STATUS_STYLE = {
  intake:         'bg-gray-100 text-[#111827] border-gray-300',
  in_review:      'bg-[#E0F2FE] text-[#075985] border-[#7DD3FC]',
  reviewed_pass:  'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]',
  further_review: 'bg-[#FFE5D9] text-[#9A3412] border-[#FDBA8C]',
  concern:        'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]',
  certified:      'bg-[#FFE5E7] text-[#9B0014] border-[#C4B5FD]',
  rejected:       'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]',
  retired:        'bg-gray-100 text-[#4B5563] border-gray-300',
};

function VerdictChip({ verdictKey }) {
  if (!verdictKey) return <span className="text-[10px] text-[#9CA3AF]">—</span>;
  const m = VERDICT_STYLE[verdictKey] || VERDICT_STYLE.monitor;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${m.tone}`}>
      <m.Icon size={11} weight="fill" /> {m.label}
    </span>
  );
}

function StatusChip({ status }) {
  if (!status) return null;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_STYLE[status] || STATUS_STYLE.intake}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

/* ---------------- BRANDS TAB ---------------- */

function BrandForm({ initial, onSaved, onClose }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    name: initial?.name || '', tier: initial?.tier || 'emerging',
    contact_name: initial?.contact_name || '', contact_email: initial?.contact_email || '',
    country: initial?.country || '', website: initial?.website || '',
    white_label: !!initial?.white_label, notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const payload = { ...form };
      if (!payload.contact_email) delete payload.contact_email;
      if (isEdit) {
        await axios.put(`${API_URL}/api/hale/brands/${initial.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/hale/brands`, payload, { withCredentials: true });
      }
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="card-flat p-4 bg-white" data-testid="hale-brand-form">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[#111827]">{isEdit ? `Edit · ${initial.name}` : 'Onboard new brand'}</h3>
        <button onClick={onClose} className="text-[#4B5563]"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Brand name *</Label>
          <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 h-9 text-sm" data-testid="hale-brand-name" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Tier *</Label>
          <select value={form.tier} onChange={(e) => setForm(f => ({ ...f, tier: e.target.value }))} className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="hale-brand-tier">
            <option value="startup">Startup</option>
            <option value="emerging">Emerging</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Country</Label>
          <Input value={form.country} onChange={(e) => setForm(f => ({ ...f, country: e.target.value }))} className="mt-1 h-9 text-sm" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Contact name</Label>
          <Input value={form.contact_name} onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))} className="mt-1 h-9 text-sm" data-testid="hale-brand-contact-name" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Contact email</Label>
          <Input type="email" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))} className="mt-1 h-9 text-sm" data-testid="hale-brand-contact-email" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Website</Label>
          <Input value={form.website} onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))} className="mt-1 h-9 text-sm" />
        </div>
        <div className="sm:col-span-2 flex items-center gap-2">
          <input type="checkbox" checked={form.white_label} onChange={(e) => setForm(f => ({ ...f, white_label: e.target.checked }))} data-testid="hale-brand-white-label" />
          <Label className="text-[12px] text-[#4B5563]">White-label badge (no Aᴳ co-branding on outputs)</Label>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Internal notes</Label>
          <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="text-[12px] mt-1" />
        </div>
      </div>
      {err && <p className="text-[11px] text-[#9B1C1C] mt-2" data-testid="hale-brand-error">{err}</p>}
      <Button onClick={save} disabled={saving || !form.name} className="mt-3 h-9 rounded bg-[#E63946] hover:bg-[#C9192C] text-white text-sm font-semibold" data-testid="hale-brand-save">
        <CheckCircle size={13} className="mr-1.5" weight="fill" /> {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Onboard brand'}
      </Button>
    </div>
  );
}

function BrandsTab({ brands, reload, onPickBrand }) {
  const [editing, setEditing] = useState(null); // null | 'new' | brand obj
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="card-flat bg-white">
        <div className="p-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#111827]">Brands ({brands.length})</h3>
          <Button onClick={() => setEditing('new')} className="h-8 text-xs rounded bg-[#E63946] hover:bg-[#C9192C] text-white" data-testid="hale-brand-new"><Plus size={12} weight="bold" className="mr-1" /> Onboard</Button>
        </div>
        <div className="divide-y divide-gray-100 max-h-[560px] overflow-y-auto">
          {brands.length === 0 ? <p className="text-sm text-[#4B5563] py-8 text-center">No brands onboarded yet.</p> :
            brands.map(b => (
              <div key={b.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50" data-testid={`hale-brand-row-${b.id}`}>
                <Buildings size={20} weight="duotone" className="text-[#E63946]" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-[#111827] truncate">{b.name}</p>
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#FFE5E7] text-[#9B0014] border border-[#C4B5FD] rounded">{b.tier}</span>
                    {b.white_label && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-gray-100 text-[#4B5563] border border-gray-300 rounded">WL</span>}
                  </div>
                  <p className="text-[11px] text-[#4B5563] truncate">{b.contact_email || '—'} · {b.sku_count || 0} SKU{b.sku_count === 1 ? '' : 's'}</p>
                </div>
                <Button onClick={() => onPickBrand(b)} variant="outline" className="h-7 text-xs rounded" data-testid={`hale-brand-view-${b.id}`}>SKUs →</Button>
                <Button onClick={() => setEditing(b)} variant="outline" className="h-7 text-xs rounded">Edit</Button>
              </div>
            ))
          }
        </div>
      </div>
      <div>
        {editing && (
          <BrandForm
            initial={editing === 'new' ? null : editing}
            onSaved={() => { setEditing(null); reload(); }}
            onClose={() => setEditing(null)}
          />
        )}
      </div>
    </div>
  );
}

/* ---------------- SKU CREATE FORM ---------------- */

function SKUCreateForm({ brands, defaultBrandId, onSaved, onClose }) {
  const [brandId, setBrandId] = useState(defaultBrandId || (brands[0]?.id || ''));
  const [productName, setProductName] = useState('');
  const [productCategory, setProductCategory] = useState('face_cream');
  const [leaveOn, setLeaveOn] = useState(true);
  const [intendedUse, setIntendedUse] = useState('');
  const [packaging, setPackaging] = useState('');
  const [rows, setRows] = useState([{ inci_name: '', concentration: 0 }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const addRow = () => setRows(rs => [...rs, { inci_name: '', concentration: 0 }]);
  const setRow = (i, patch) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));

  const save = async () => {
    setErr(''); setSaving(true);
    const ingredients = rows
      .filter(r => r.inci_name?.trim() && parseFloat(r.concentration) > 0)
      .map(r => ({ inci_name: r.inci_name.trim(), concentration: parseFloat(r.concentration) }));
    if (!brandId) { setErr('Pick a brand.'); setSaving(false); return; }
    if (!productName.trim()) { setErr('Product name required.'); setSaving(false); return; }
    if (ingredients.length === 0) { setErr('At least one ingredient required.'); setSaving(false); return; }
    try {
      await axios.post(`${API_URL}/api/hale/skus`, {
        brand_id: brandId,
        product_name: productName.trim(),
        product_category: productCategory,
        intended_use: intendedUse || null,
        leave_on: leaveOn,
        ingredients,
        packaging_materials: packaging.split(',').map(s => s.trim()).filter(Boolean),
      }, { withCredentials: true });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="card-flat p-4 bg-white" data-testid="hale-sku-form">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[#111827]">Intake new SKU</h3>
        <button onClick={onClose} className="text-[#4B5563]"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Brand *</Label>
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)} className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="hale-sku-brand">
            <option value="">— pick a brand —</option>
            {brands.map(b => <option key={b.id} value={b.id}>{b.name} · {b.tier}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Product name *</Label>
          <Input value={productName} onChange={(e) => setProductName(e.target.value)} className="mt-1 h-9 text-sm" placeholder="e.g. Daily Vitamin C Serum" data-testid="hale-sku-name" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Category</Label>
          <Input value={productCategory} onChange={(e) => setProductCategory(e.target.value)} className="mt-1 h-9 text-sm" data-testid="hale-sku-category" />
        </div>
        <div className="flex items-end gap-2">
          <label className="flex items-center gap-1.5 text-[12px] text-[#4B5563]">
            <input type="checkbox" checked={leaveOn} onChange={(e) => setLeaveOn(e.target.checked)} data-testid="hale-sku-leave-on" />
            Leave-on product (else rinse-off)
          </label>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Intended use</Label>
          <Input value={intendedUse} onChange={(e) => setIntendedUse(e.target.value)} className="mt-1 h-9 text-sm" placeholder="e.g. Daily AM serum, full-face application" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Packaging materials (comma-separated)</Label>
          <Input value={packaging} onChange={(e) => setPackaging(e.target.value)} className="mt-1 h-9 text-sm" placeholder="PET dropper bottle, Aluminum cap, FSC kraft carton" data-testid="hale-sku-packaging" />
        </div>
      </div>

      <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Ingredients *</Label>
      <div className="space-y-1.5 mt-1">
        {rows.map((row, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input value={row.inci_name} onChange={(e) => setRow(i, { inci_name: e.target.value })} placeholder="INCI name" className="h-8 text-sm flex-1" data-testid={`hale-sku-inci-${i}`} />
            <Input type="number" step="0.01" value={row.concentration} onChange={(e) => setRow(i, { concentration: e.target.value })} placeholder="0" className="h-8 text-sm w-24 text-right tabular-nums" data-testid={`hale-sku-conc-${i}`} />
            <span className="text-[10px] text-[#6B7280] w-4">%</span>
            <button type="button" onClick={() => removeRow(i)} className="text-[#9B1C1C]"><X size={12} /></button>
          </div>
        ))}
      </div>
      <Button onClick={addRow} variant="outline" className="h-7 text-xs rounded mt-2" data-testid="hale-sku-add-ingredient"><Plus size={11} weight="bold" className="mr-1" /> Add row</Button>

      {err && <p className="text-[11px] text-[#9B1C1C] mt-2" data-testid="hale-sku-error">{err}</p>}
      <Button onClick={save} disabled={saving} className="mt-3 h-9 rounded bg-[#E63946] hover:bg-[#C9192C] text-white text-sm font-semibold" data-testid="hale-sku-save">
        {saving ? 'Saving…' : 'Intake SKU'}
      </Button>
    </div>
  );
}

/* ---------------- SKU DETAIL PANEL ---------------- */

function SKUDetail({ skuId, onAfterAction, onClose }) {
  const [sku, setSku] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState('');

  // Verdict-set state
  const [verdictKey, setVerdictKey] = useState('no_concern');
  const [summary, setSummary] = useState('');
  const [nextSteps, setNextSteps] = useState('');
  const [reformulations, setReformulations] = useState('');
  const [escalation, setEscalation] = useState(false);
  const [savingVerdict, setSavingVerdict] = useState(false);

  const load = useCallback(async () => {
    if (!skuId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/hale/skus/${skuId}`, { withCredentials: true });
      setSku(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [skuId]);

  useEffect(() => { load(); }, [load]);

  const runScreen = async () => {
    setRunning(true); setErr('');
    try {
      const r = await axios.post(`${API_URL}/api/hale/skus/${skuId}/run-screen`, {}, { withCredentials: true });
      setSku(r.data);
      onAfterAction?.();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setRunning(false); }
  };

  const saveVerdict = async () => {
    if (summary.trim().length < 10) { setErr('Verdict summary requires ≥10 chars.'); return; }
    setSavingVerdict(true); setErr('');
    try {
      const r = await axios.post(`${API_URL}/api/hale/skus/${skuId}/set-verdict`, {
        verdict: verdictKey,
        summary: summary.trim(),
        recommended_next_steps: nextSteps.split('\n').map(s => s.trim()).filter(Boolean),
        reformulation_recommendations: reformulations.split('\n').map(s => s.trim()).filter(Boolean),
        escalation_recommended: escalation,
      }, { withCredentials: true });
      setSku(r.data);
      onAfterAction?.();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setSavingVerdict(false); }
  };

  if (loading || !sku) return <div className="card-flat p-6 bg-white text-center text-[#4B5563] text-sm" data-testid="hale-sku-detail-loading">Loading…</div>;
  const v = sku.verdict;

  return (
    <div className="space-y-4">
      <div className="card-flat p-5 bg-white" data-testid="hale-sku-detail">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">{sku.brand?.name || '—'} · {sku.brand?.tier || ''}</p>
            <h3 className="text-base font-bold text-[#111827]">{sku.product_name}</h3>
            <p className="text-[11px] text-[#4B5563]">{sku.product_category} · {sku.leave_on ? 'leave-on' : 'rinse-off'} · {sku.ingredients?.length || 0} ingredients</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusChip status={sku.status} />
            {v?.key && <VerdictChip verdictKey={v.key} />}
            <button onClick={onClose} className="text-[#4B5563]"><X size={16} /></button>
          </div>
        </div>

        {sku.ingredients?.length > 0 && (
          <div className="border border-gray-200 rounded overflow-hidden mb-3 max-h-44 overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-gray-50">
                <tr className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">
                  <th className="text-left py-1.5 px-2">INCI</th>
                  <th className="text-right py-1.5 px-2 w-16">Conc</th>
                </tr>
              </thead>
              <tbody>
                {sku.ingredients.map((ing, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="py-1.5 px-2 text-[#111827]">{ing.inci_name}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{ing.concentration}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {sku.packaging_materials?.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">Packaging</p>
            <div className="flex flex-wrap gap-1">
              {sku.packaging_materials.map((m, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50">{m}</span>)}
            </div>
          </div>
        )}

        {/* Run screen */}
        {!v && (
          <Button onClick={runScreen} disabled={running} className="h-9 rounded bg-[#0B1426] hover:bg-[#1B2538] text-white text-sm font-semibold" data-testid="hale-sku-run-screen">
            <Flask size={13} weight="fill" className="mr-1.5" /> {running ? 'Running screen…' : 'Run Hale Reviewed screen'}
          </Button>
        )}

        {v && (
          <div className="border-l-4 border-[#E63946] bg-[#F5F3FF] rounded p-3 mb-3" data-testid="hale-verdict-card">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#9B0014] mb-1">Engine verdict</p>
            <VerdictChip verdictKey={v.key} />
            <p className="text-[11px] text-[#4B5563] mt-2 tabular-nums">
              Overall: <strong>{v.overall_status}</strong> · Flagged ingredients: <strong>{v.flagged_count ?? 0}</strong>
            </p>
            {v.contaminants && Object.keys(v.contaminants).length > 0 && (
              <p className="text-[11px] text-[#9B1C1C] mt-1">
                Contaminant pathways: <strong>{Object.keys(v.contaminants).join(' · ')}</strong>
              </p>
            )}
            <p className="text-[10px] text-[#4B5563] mt-1">Screened by {v.screened_by} · {v.screened_at && new Date(v.screened_at).toLocaleString()}</p>
            <Button onClick={runScreen} disabled={running} variant="outline" className="h-7 text-xs rounded mt-2" data-testid="hale-sku-rescreen"><ArrowsClockwise size={10} weight="bold" className="mr-1" /> Re-run screen</Button>
          </div>
        )}

        {err && <p className="text-[11px] text-[#9B1C1C] mt-2" data-testid="hale-sku-error">{err}</p>}
      </div>

      {/* Finalise verdict */}
      {v && !v.finalised_at && (
        <div className="card-flat p-5 bg-white" data-testid="hale-verdict-form">
          <h4 className="text-sm font-bold text-[#111827] mb-2">Finalise reviewer verdict</h4>
          <p className="text-[11px] text-[#4B5563] mb-3">The engine recommended <VerdictChip verdictKey={v.key} />. Confirm, override, and add the reviewer memo.</p>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Verdict</Label>
          <select value={verdictKey} onChange={(e) => setVerdictKey(e.target.value)} className="mt-1 mb-3 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="hale-verdict-key">
            {Object.entries(VERDICT_STYLE).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Reviewer summary *</Label>
          <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} className="text-[12px] mt-1 mb-3" placeholder="e.g. Ingredient screen identified Diethyl phthalate; recommend reformulation to phthalate-free fragrance carrier. No EU Annex II hits…" data-testid="hale-verdict-summary" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Recommended next steps</Label>
              <Textarea value={nextSteps} onChange={(e) => setNextSteps(e.target.value)} rows={3} className="text-[11px] mt-1" placeholder="One per line" data-testid="hale-verdict-next-steps" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Reformulation recommendations</Label>
              <Textarea value={reformulations} onChange={(e) => setReformulations(e.target.value)} rows={3} className="text-[11px] mt-1" placeholder="One per line" data-testid="hale-verdict-reformulations" />
            </div>
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-[#4B5563] mb-3">
            <input type="checkbox" checked={escalation} onChange={(e) => setEscalation(e.target.checked)} data-testid="hale-verdict-escalation" />
            Escalation recommended (counsel / retailer review)
          </label>
          <Button onClick={saveVerdict} disabled={savingVerdict} className="h-9 rounded bg-[#E63946] hover:bg-[#C9192C] text-white text-sm font-semibold" data-testid="hale-verdict-save">
            <CheckCircle size={13} weight="fill" className="mr-1.5" /> {savingVerdict ? 'Finalising…' : 'Finalise verdict + memo'}
          </Button>
        </div>
      )}

      {/* Final verdict view */}
      {v?.finalised_at && (
        <div className="card-flat p-5 bg-[#F5F3FF] border-l-4 border-[#E63946]" data-testid="hale-verdict-finalised">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#9B0014] mb-1">Reviewer verdict — finalised</p>
          <VerdictChip verdictKey={v.key} />
          <p className="text-[12px] text-[#111827] mt-3 leading-relaxed whitespace-pre-line" data-testid="hale-final-summary">{v.summary}</p>
          {v.recommended_next_steps?.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">Recommended next steps</p>
              <ul className="space-y-0.5 text-[11px] text-[#4B5563] list-disc list-inside">
                {v.recommended_next_steps.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {v.reformulation_recommendations?.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">Reformulation recommendations</p>
              <ul className="space-y-0.5 text-[11px] text-[#4B5563] list-disc list-inside">
                {v.reformulation_recommendations.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {v.escalation_recommended && (
            <p className="mt-3 text-[11px] text-[#9B1C1C] font-bold flex items-center gap-1.5"><WarningCircle size={12} weight="fill" /> Escalation recommended</p>
          )}
          <p className="text-[10px] text-[#4B5563] mt-3">Finalised by {v.finalised_by} · {new Date(v.finalised_at).toLocaleString()}</p>
        </div>
      )}

      {/* Audit */}
      {sku.audit?.length > 0 && (
        <div className="card-flat p-4 bg-white">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-2">Audit ({sku.audit.length})</p>
          <div className="space-y-1.5">
            {sku.audit.slice().reverse().map((a, i) => (
              <div key={i} className="text-[11px] text-[#4B5563] border-b border-gray-100 pb-1.5">
                <span className="font-mono text-[10px] bg-gray-100 px-1 rounded">{a.action}</span>
                <span className="ml-2">{a.by}</span>
                <span className="ml-2 text-[#9CA3AF]">· {new Date(a.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- ROOT PAGE ---------------- */

export default function HaleAdminPage() {
  const [tab, setTab] = useState('queue');
  const [brands, setBrands] = useState([]);
  const [skus, setSkus] = useState([]);
  const [counters, setCounters] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [selectedSku, setSelectedSku] = useState(null);
  const [showIntake, setShowIntake] = useState(false);
  const [defaultBrandIdForIntake, setDefaultBrandIdForIntake] = useState('');

  const loadBrands = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/hale/brands?limit=200`, { withCredentials: true });
      setBrands(r.data.items || []);
    } catch (e) { /* keep */ }
  }, []);

  const loadSkus = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (brandFilter) params.set('brand_id', brandFilter);
      params.set('limit', '100');
      const r = await axios.get(`${API_URL}/api/hale/skus?${params}`, { withCredentials: true });
      setSkus(r.data.items || []);
      setCounters(r.data.counters || {});
    } catch (e) { /* keep */ }
  }, [statusFilter, brandFilter]);

  useEffect(() => { loadBrands(); }, [loadBrands]);
  useEffect(() => { loadSkus(); }, [loadSkus]);

  const brandsMap = useMemo(() => Object.fromEntries(brands.map(b => [b.id, b])), [brands]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <HaleWordmark size="md" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0B0B0B] bg-[#FFE5E7] border border-[#E63946]/40 px-2 py-0.5 rounded">Reviewed Console · Phase H1</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={22} className="text-[#E63946]" weight="duotone" />
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">Hale · Reviewed Console</h1>
          </div>
          <p className="text-sm text-[#4B5563]">
            Brand onboarding, SKU intake, Hale Reviewed engine runs, reviewer verdicts, and audit trail — powered by the ToxIntel scientific stack.
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Link to="/assessor"><Button variant="outline" className="h-8 text-xs rounded">← Assessor Console</Button></Link>
            <Link to="/hale" target="_blank"><Button variant="outline" className="h-8 text-xs rounded" data-testid="hale-public-link">View Hale partner page ↗</Button></Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-4">
          {[
            { id: 'queue',  label: `SKU Queue (${counters.total ?? 0})` },
            { id: 'brands', label: `Brands (${brands.length})` },
          ].map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setSelectedSku(null); }} className={`px-4 py-2.5 text-sm font-semibold border-b-2 ${tab === t.id ? 'border-[#E63946] text-[#111827]' : 'border-transparent text-[#4B5563] hover:text-[#111827]'}`} data-testid={`hale-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'brands' && (
          <BrandsTab brands={brands} reload={() => { loadBrands(); loadSkus(); }} onPickBrand={(b) => { setBrandFilter(b.id); setTab('queue'); }} />
        )}

        {tab === 'queue' && (
          <>
            {/* Counters */}
            <div className="grid grid-cols-3 sm:grid-cols-8 gap-2 mb-4">
              {[
                { id: '',               label: 'All',            val: counters.total },
                { id: 'intake',         label: 'Intake',         val: counters.intake },
                { id: 'in_review',      label: 'In review',      val: counters.in_review },
                { id: 'reviewed_pass',  label: 'Reviewed',       val: counters.reviewed_pass },
                { id: 'further_review', label: 'Further',        val: counters.further_review },
                { id: 'concern',        label: 'Concern',        val: counters.concern },
                { id: 'certified',      label: 'Certified',      val: counters.certified },
                { id: 'rejected',       label: 'Rejected',       val: counters.rejected },
              ].map(c => (
                <button key={c.id || 'all'} onClick={() => setStatusFilter(c.id)} className={`p-2 rounded border text-left ${statusFilter === c.id ? 'border-[#E63946] bg-[#F5F3FF]' : 'border-gray-200 bg-white'}`} data-testid={`hale-counter-${c.id || 'all'}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">{c.label}</p>
                  <p className="text-lg font-black tabular-nums text-[#111827]">{c.val ?? 0}</p>
                </button>
              ))}
            </div>

            {/* Action row */}
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Brand filter</Label>
                <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="hale-brand-filter">
                  <option value="">All brands</option>
                  {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <Button onClick={() => { setDefaultBrandIdForIntake(brandFilter); setShowIntake(true); }} className="h-9 rounded bg-[#E63946] hover:bg-[#C9192C] text-white text-sm font-semibold" data-testid="hale-intake-new"><Plus size={13} weight="bold" className="mr-1" /> Intake SKU</Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* SKU list */}
              <div className="lg:col-span-2 card-flat bg-white">
                <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                  {skus.length === 0 ? <p className="text-sm text-[#4B5563] py-8 text-center">No SKUs match.</p> :
                    skus.map(s => (
                      <button key={s.id} onClick={() => setSelectedSku(s.id)} className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 ${selectedSku === s.id ? 'bg-[#F5F3FF]' : ''}`} data-testid={`hale-sku-row-${s.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-[#111827] truncate flex-1">{s.product_name}</p>
                          <StatusChip status={s.status} />
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] text-[#4B5563]">{s.brand?.name || brandsMap[s.brand_id]?.name || '—'}</span>
                          <span className="text-[10px] text-[#9CA3AF]">·</span>
                          <span className="text-[10px] text-[#4B5563]">{s.ingredients_count} ing</span>
                          {s.verdict?.key && <VerdictChip verdictKey={s.verdict.key} />}
                        </div>
                      </button>
                    ))
                  }
                </div>
              </div>

              {/* Detail */}
              <div className="lg:col-span-3">
                {showIntake ? (
                  <SKUCreateForm
                    brands={brands} defaultBrandId={defaultBrandIdForIntake}
                    onSaved={() => { setShowIntake(false); loadSkus(); loadBrands(); }}
                    onClose={() => setShowIntake(false)}
                  />
                ) : selectedSku ? (
                  <SKUDetail skuId={selectedSku} onAfterAction={() => loadSkus()} onClose={() => setSelectedSku(null)} />
                ) : (
                  <div className="card-flat p-6 bg-white text-center text-[#4B5563] text-sm flex flex-col items-center gap-2">
                    <Eye size={32} weight="duotone" className="text-[#9CA3AF]" />
                    Pick a SKU to review or intake a new one.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
